import { type UserHistoryDto } from "@crowdcoursing/api/openapi";
import { Box, type BoxProps, Grid, Typography } from "@mui/material";
import isEmpty from "lodash/isEmpty";
import { useMemo } from "react";

import { formatMinuteStat, formatToMoney } from "~ui/pages/AdminPage/utils";

const tThisWeek = "This Week";
const tThisMonth = "This Month";
const tThisYear = "This Year";
const tAllTime = "All Time";
const tNoStats = "No volunteer stats";

function Container(props: BoxProps) {
  return <Box {...props}>{props.children}</Box>;
}

export interface UserHistoryStatsBaseProps {
  volunteerStats: NonNullable<UserHistoryDto["volunteerStats"]>;
}
export interface UserHistoryStatsSlotProps {
  slotProps?: { container?: BoxProps };
}
export type UserHistoryStatsProps = UserHistoryStatsBaseProps & UserHistoryStatsSlotProps;

export const UserHistoryVolunteerStats = ({ volunteerStats, slotProps }: UserHistoryStatsProps) => {
  const stats = useMemo(
    function () {
      return [
        {
          label: tThisWeek,
          value: `${formatMinuteStat(
            volunteerStats.minutesThisWeek,
          )} (${formatToMoney(volunteerStats.economicImpactWeek ?? 0)})`,
        },
        {
          label: tThisMonth,
          value: `${formatMinuteStat(
            volunteerStats.minutesThisMonth,
          )} (${formatToMoney(volunteerStats.economicImpactMonth ?? 0)})`,
        },
        {
          label: tThisYear,
          value: `${formatMinuteStat(
            volunteerStats.minutesThisYear,
          )} (${formatToMoney(volunteerStats.economicImpactYTD ?? 0)})`,
        },
        {
          label: tAllTime,
          value: `${formatMinuteStat(
            volunteerStats.minutesAllTime,
          )} (${formatToMoney(volunteerStats.economicImpactAllTime ?? 0)})`,
        },
      ];
    },
    [volunteerStats],
  );

  if (isEmpty(volunteerStats)) {
    return (
      <Container {...slotProps?.container}>
        <Typography sx={{ fontStyle: "italic" }}>{tNoStats}</Typography>
      </Container>
    );
  }

  return (
    <Container {...slotProps?.container}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          maxHeight: "350px",
          overflowY: "auto",
          paddingBottom: 2,
          paddingTop: 2,
          px: 2,
        }}
      >
        <Grid container spacing={3}>
          {stats.slice(0, 3).map(function ({ label, value }, index) {
            return (
              <Grid
                key={`${label}-${index}`}
                size={{
                  md: 4,
                  xs: 6,
                }}
              >
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    color="text.secondary"
                    sx={{ fontWeight: "bold", mb: 0.5, textAlign: "center" }}
                    variant="body2"
                  >
                    {label}
                  </Typography>
                  <Typography sx={{ textAlign: "center" }}>{value}</Typography>
                </Box>
              </Grid>
            );
          })}
          <Grid
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: { md: "1em" },
            }}
            size={{
              md: 12,
              xs: 6,
            }}
          >
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                color="text.secondary"
                sx={{ fontWeight: "bold", mb: 0.5, textAlign: "center" }}
                variant="body2"
              >
                {stats[3].label}
              </Typography>
              <Typography sx={{ textAlign: "center" }}>{stats[3].value}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};
